import type { Clock } from "../ports/clock";
import type {
  CheckInRepository,
  GoalRepository,
  OnboardingWeightReferenceRepository,
  WeekPlanRepository,
  WeightRepository,
} from "../ports/repositories";
import type { PlanningConfiguration, PlanningConfigurationProvider } from "../ports/planning-configuration";
import { addCalendarDays } from "../../domain/shared/calendar-date";
import { fiveCalendarDayWeightTrend, type WeightTrend } from "../../domain/weight/rolling-average";
import { recommendDailyCalories, type CalorieRecommendation, type CheckInQuestionnaire } from "../../domain/check-in/recommendation-policy";
import { calculateProteinTarget, type ProteinTargetResult } from "../../domain/goals/protein-policy";
import type { GoalPhase } from "../../domain/goals/goal";
import type { WeekPlan } from "../../domain/planning/week-plan";
import { calculateDailyCalorieFloor } from "../../domain/planning/daily-floor-policy";
import { checkInStateOn, type CheckInState } from "../../domain/check-in/state-machine";
import {
  snapshotOnboardingTrend,
  snapshotPreviousCheckInTrend,
  type CheckInTrendSnapshot,
} from "../../domain/check-in/check-in";

export interface PreparedCheckIn {
  readonly plan: WeekPlan;
  readonly state: CheckInState;
  readonly goal: GoalPhase;
  readonly questionnaire: CheckInQuestionnaire;
  readonly previousTrend?: CheckInTrendSnapshot;
  readonly currentTrend?: WeightTrend;
  readonly recommendation: CalorieRecommendation;
  readonly proteinTarget: ProteinTargetResult;
  readonly configuration: PlanningConfiguration;
}

export interface PrepareCheckInDependencies {
  readonly clock: Clock;
  readonly weights: WeightRepository;
  readonly onboardingReference: OnboardingWeightReferenceRepository;
  readonly weekPlans: WeekPlanRepository;
  readonly checkIns: CheckInRepository;
  readonly goals: GoalRepository;
  readonly configuration: PlanningConfigurationProvider;
}

export async function prepareCheckIn(
  dependencies: PrepareCheckInDependencies,
  questionnaire: CheckInQuestionnaire,
): Promise<PreparedCheckIn> {
  const today = dependencies.clock.today();
  const [plan, previousCheckIn, onboardingReference, goal, configuration, recentWeights] = await Promise.all([
    dependencies.weekPlans.getOpen(),
    dependencies.checkIns.getLatestCompleted(),
    dependencies.onboardingReference.get(),
    dependencies.goals.getActive(today),
    dependencies.configuration.get(),
    dependencies.weights.listBetween(addCalendarDays(today, -4), today),
  ]);
  if (!plan) throw new Error("No open week plan exists");
  if (!goal) throw new Error("No active weight goal exists");
  const state = checkInStateOn(plan, today);
  if (state === "active") throw new Error("Weekly check-in is not due yet");
  if (state === "completed") throw new Error("Week plan has already been checked in");

  const currentTrend = fiveCalendarDayWeightTrend(recentWeights, today);
  const previousTrend = previousCheckIn
    ? (previousCheckIn.currentTrend ? snapshotPreviousCheckInTrend(previousCheckIn.currentTrend) : undefined)
    : (onboardingReference ? snapshotOnboardingTrend(onboardingReference) : undefined);
  const rawRecommendation = recommendDailyCalories({
    previousBaselineCalories: plan.baselineDailyCalories,
    goal,
    questionnaire,
    previousTrend: previousTrend ? { date: previousTrend.date, kilograms: previousTrend.averageKilograms } : undefined,
    currentTrend: currentTrend ? { date: currentTrend.date, kilograms: currentTrend.averageKilograms } : undefined,
    policy: configuration.recommendationPolicy,
  });
  const proteinTarget = calculateProteinTarget({
    goalMode: goal.mode,
    referenceTrendWeightKilograms: currentTrend?.averageKilograms,
    referenceTrendDate: currentTrend?.date,
    previousDailyProteinGrams: plan.fixedDailyProteinGrams,
  });
  const dailyFloor = calculateDailyCalorieFloor(
    configuration.dailyFloorPolicy,
    proteinTarget.gramsPerDay,
    configuration.macroDistribution,
  );
  const recommendation: CalorieRecommendation = rawRecommendation.recommendedBaselineCalories >= dailyFloor
    ? rawRecommendation
    : {
        ...rawRecommendation,
        recommendedBaselineCalories: dailyFloor,
        dailyAdjustmentCalories: dailyFloor - plan.baselineDailyCalories,
        uncappedDailyAdjustmentCalories: Math.max(
          rawRecommendation.uncappedDailyAdjustmentCalories,
          dailyFloor - plan.baselineDailyCalories,
        ),
        reason: "daily_floor",
      };
  return { plan, state, goal, questionnaire, previousTrend, currentTrend, recommendation, proteinTarget, configuration };
}
